import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Sparkle, X, Plus, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { getEuropeSlots, saveEuropeSlots, verifyEuropeSlots } from "../lib/api";

const TIER_OPTIONS = [
    { value: "CL", label: "CL · Champions League" },
    { value: "EL", label: "EL · Europa League" },
    { value: "ECL", label: "ECL · Conference League" },
];

export default function EuropeSlotsAdmin({ teamChoices = [] }) {
    const [doc, setDoc] = useState(null);
    const [extras, setExtras] = useState(0);
    const [coefBonus, setCoefBonus] = useState(0);
    const [autoQuals, setAutoQuals] = useState([]);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        getEuropeSlots()
            .then((d) => {
                setDoc(d);
                setExtras(d.extra_europe_slots || 0);
                setCoefBonus(d.uefa_coefficient_bonus || 0);
                setAutoQuals(d.automatic_qualifiers || []);
                setNotes(d.notes || "");
            })
            .catch(() => toast.error("Failed to load Europe slot config"));
    }, []);

    const hasOverrides = extras > 0 || coefBonus > 0 || autoQuals.length > 0;

    const addQualifier = () => {
        setAutoQuals((prev) => [...prev, { team_name: "", tier: "EL", reason: "" }]);
    };
    const removeQualifier = (idx) => {
        setAutoQuals((prev) => prev.filter((_, i) => i !== idx));
    };
    const updateQualifier = (idx, field, value) => {
        setAutoQuals((prev) =>
            prev.map((q, i) => (i === idx ? { ...q, [field]: value } : q))
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const cleaned = autoQuals.filter((q) => q.team_name?.trim());
            const updated = await saveEuropeSlots({
                extra_europe_slots: extras,
                automatic_qualifiers: cleaned,
                uefa_coefficient_bonus: coefBonus,
                notes,
            });
            setDoc(updated);
            toast.success("Europe slots config saved · reload page to recompute race");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleVerify = async () => {
        setVerifying(true);
        setVerifyResult(null);
        try {
            const result = await verifyEuropeSlots();
            setVerifyResult(result);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verify failed");
        } finally {
            setVerifying(false);
        }
    };

    if (!doc) return null;

    return (
        <section
            className="border border-fa-violet/30 bg-[#0d0d0d] p-4"
            data-testid="europe-slots-admin"
        >
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between gap-2"
                data-testid="europe-slots-admin-toggle"
            >
                <div className="flex items-center gap-2">
                    <Trophy size={14} weight="fill" className="text-fa-violet" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-fa-violet">
                        Europe Slots Config · {doc.season}
                    </span>
                    {hasOverrides && (
                        <span className="font-mono text-[9px] uppercase tracking-widest text-fa-yellow border border-fa-yellow/40 px-1.5 py-0.5">
                            {extras > 0 && `+${extras} slot${extras === 1 ? "" : "s"}`}
                            {autoQuals.length > 0 && ` · ${autoQuals.length} auto-qual`}
                        </span>
                    )}
                </div>
                <span className="font-mono text-[10px] text-white/40">
                    {expanded ? "Collapse −" : "Expand +"}
                </span>
            </button>

            {expanded && (
                <div className="mt-4 space-y-4">
                    <p className="font-mono text-[11px] text-white/60 leading-snug">
                        Default cutoff is top-7 (4 CL · 2 EL · 1 ECL). Adjust when domestic cup winners are already qualified through the league (FA Cup, EFL Cup) — the extra Europe slot drops to the highest-placed non-qualified team. List PL teams that have <strong>won</strong> (not just reached the final of) UEFA competitions.
                    </p>

                    {/* Extra slots */}
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                            Extra Europe slots (FA Cup / EFL Cup winner already qualified)
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="3"
                            value={extras}
                            onChange={(e) => setExtras(Math.max(0, Math.min(3, Number(e.target.value) || 0)))}
                            className="w-24 bg-black border border-white/10 rounded-none font-mono text-sm px-3 py-2 text-white focus:outline-none focus:border-fa-violet"
                            data-testid="extra-slots-input"
                        />
                    </div>

                    {/* UEFA coefficient bonus */}
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                            UEFA coefficient bonus CL slots
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="2"
                            value={coefBonus}
                            onChange={(e) => setCoefBonus(Math.max(0, Math.min(2, Number(e.target.value) || 0)))}
                            className="w-24 bg-black border border-white/10 rounded-none font-mono text-sm px-3 py-2 text-white focus:outline-none focus:border-fa-violet"
                            data-testid="coef-bonus-input"
                        />
                    </div>

                    {/* Automatic qualifiers */}
                    <div>
                        <div className="flex items-baseline justify-between mb-2">
                            <label className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                                Automatic qualifiers (UEFA cup winners)
                            </label>
                            <button
                                type="button"
                                onClick={addQualifier}
                                className="font-mono text-[10px] uppercase tracking-widest text-fa-violet hover:text-white flex items-center gap-1"
                                data-testid="add-qualifier-btn"
                            >
                                <Plus size={10} weight="bold" /> Add team
                            </button>
                        </div>
                        <div className="space-y-2" data-testid="auto-quals-list">
                            {autoQuals.length === 0 && (
                                <div className="font-mono text-[10px] text-white/30 italic">
                                    None — add e.g. Europa League winner or CL winner here.
                                </div>
                            )}
                            {autoQuals.map((q, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`qualifier-row-${i}`}>
                                    <input
                                        list="europe-slots-team-choices"
                                        type="text"
                                        value={q.team_name}
                                        onChange={(e) => updateQualifier(i, "team_name", e.target.value)}
                                        placeholder="Team"
                                        className="col-span-4 bg-black border border-white/10 font-mono text-xs px-2 py-1.5 text-white focus:outline-none focus:border-fa-violet"
                                        data-testid={`qual-team-${i}`}
                                    />
                                    <select
                                        value={q.tier}
                                        onChange={(e) => updateQualifier(i, "tier", e.target.value)}
                                        className="col-span-3 bg-black border border-white/10 font-mono text-xs px-2 py-1.5 text-white focus:outline-none focus:border-fa-violet"
                                        data-testid={`qual-tier-${i}`}
                                    >
                                        {TIER_OPTIONS.map((t) => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        value={q.reason}
                                        onChange={(e) => updateQualifier(i, "reason", e.target.value)}
                                        placeholder="Reason (e.g. EL winner)"
                                        className="col-span-4 bg-black border border-white/10 font-mono text-xs px-2 py-1.5 text-white focus:outline-none focus:border-fa-violet"
                                        data-testid={`qual-reason-${i}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeQualifier(i)}
                                        className="col-span-1 text-white/40 hover:text-[#FF6B6B]"
                                        data-testid={`qual-remove-${i}`}
                                    >
                                        <X size={14} weight="bold" />
                                    </button>
                                </div>
                            ))}
                            <datalist id="europe-slots-team-choices">
                                {teamChoices.map((t) => <option key={t} value={t} />)}
                            </datalist>
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                            Admin notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder="e.g. Man City won FA Cup already top-4 · Villa hosts EL final 21 May"
                            className="w-full bg-black border border-white/10 rounded-none font-mono text-xs px-3 py-2 text-white focus:outline-none focus:border-fa-violet resize-none"
                            data-testid="notes-input"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/5">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-fa-violet text-white font-display font-black uppercase tracking-wide text-sm px-5 py-2 hover:bg-fa-violet/90 transition-colors disabled:opacity-30"
                            data-testid="save-europe-slots-btn"
                        >
                            {saving ? "Saving…" : "Save config"}
                        </button>
                        <button
                            type="button"
                            onClick={handleVerify}
                            disabled={verifying}
                            className="border border-fa-yellow/60 text-fa-yellow font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-fa-yellow/10 transition-colors disabled:opacity-30 flex items-center gap-1.5"
                            data-testid="verify-europe-slots-btn"
                        >
                            <Sparkle size={11} weight="fill" />
                            {verifying ? "Verifying with AI…" : "Verify with AI"}
                        </button>
                        {doc.updated_at && (
                            <span className="font-mono text-[10px] text-white/30">
                                last saved {new Date(doc.updated_at).toLocaleString()}
                            </span>
                        )}
                    </div>

                    {/* Verify result */}
                    {verifyResult && (
                        <div
                            className={`border-l-2 pl-3 py-2 space-y-2 ${
                                verifyResult.warnings?.length > 0
                                    ? "border-fa-yellow bg-fa-yellow/5"
                                    : "border-fa-mint bg-fa-mint/5"
                            }`}
                            data-testid="verify-result"
                        >
                            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest">
                                {verifyResult.warnings?.length > 0 ? (
                                    <>
                                        <WarningCircle size={12} weight="fill" className="text-fa-yellow" />
                                        <span className="text-fa-yellow">AI verdict · {verifyResult.warnings.length} warning{verifyResult.warnings.length === 1 ? "" : "s"}</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle size={12} weight="fill" className="text-fa-mint" />
                                        <span className="text-fa-mint">AI verdict · looks consistent</span>
                                    </>
                                )}
                            </div>
                            <p className="font-mono text-[11px] text-white/80 italic">
                                "{verifyResult.verdict}"
                            </p>
                            {verifyResult.warnings?.length > 0 && (
                                <ul className="space-y-0.5">
                                    {verifyResult.warnings.map((w, i) => (
                                        <li key={i} className="font-mono text-[11px] text-fa-yellow/90 pl-2">
                                            ⚠ {w}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {verifyResult.suggestions?.length > 0 && (
                                <ul className="space-y-0.5">
                                    {verifyResult.suggestions.map((s, i) => (
                                        <li key={i} className="font-mono text-[11px] text-white/60 pl-2">
                                            → {s}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

export function EuropeSlotsBanner({ europeSlots }) {
    if (!europeSlots) return null;
    const extras = europeSlots.extra_slots || 0;
    const coef = europeSlots.coefficient_bonus || 0;
    const autos = europeSlots.automatic_qualifiers || [];
    if (extras === 0 && coef === 0 && autos.length === 0) return null;
    return (
        <div
            className="border border-fa-violet/40 bg-fa-violet/5 p-3 space-y-1.5"
            data-testid="europe-slots-banner"
        >
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-fa-violet">
                <Trophy size={11} weight="fill" />
                Adjusted European qualification ({europeSlots.cutoff_position}-team cutoff)
            </div>
            <ul className="space-y-0.5 font-mono text-[11px] text-white/70">
                {extras > 0 && (
                    <li data-testid="banner-extras">
                        +{extras} European slot{extras === 1 ? "" : "s"} via domestic cup winner(s) already qualified
                    </li>
                )}
                {coef > 0 && (
                    <li data-testid="banner-coef">
                        +{coef} CL slot{coef === 1 ? "" : "s"} via UEFA coefficient bonus
                    </li>
                )}
                {autos.map((q) => (
                    <li key={q.team_name} data-testid={`banner-auto-${q.team_name}`}>
                        <span style={{ color: "#9DB7F5" }}>{q.team_name}</span> auto-qualified ({q.tier}) — {q.reason || "UEFA winner"}
                    </li>
                ))}
            </ul>
            {europeSlots.notes && (
                <p className="font-mono text-[10px] text-white/40 italic pt-1 border-t border-white/5">
                    {europeSlots.notes}
                </p>
            )}
        </div>
    );
}
